import React, { useState, useEffect, useMemo } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  UncontrolledAlert,
  Spinner,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import Switch from "react-switch"
import "./RegistrarUsuario.css"

const RegistrarUsuario = ({ setBreadcrumbItems, user }) => {
  const navigate = useNavigate()
  document.title = "Registrar Usuario | LS-DA - Softlogy"

  // ── Stepper ──
  const [step, setStep] = useState(1)

  // ── Step 1: User data ──
  const [nombre, setNombre] = useState("")
  const [apellido, setApellido] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [telefono, setTelefono] = useState("")
  const [esSuperAdmin, setEsSuperAdmin] = useState(false)

  // ── Step 2: Empresas assignment ──
  const [empresas, setEmpresas] = useState([])
  const [empresasLoading, setEmpresasLoading] = useState(false)
  const [searchEmpresa, setSearchEmpresa] = useState("")
  const [selectedEmpresas, setSelectedEmpresas] = useState({})
  // selectedEmpresas shape: { [empresaId]: { esAdminEmpresa: bool, todasSucursales: bool } }

  // ── General state ──
  const [loading, setLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  // ── Breadcrumb ──
  useEffect(() => {
    const greeting = user
      ? `Bienvenido, ${user.nombre} ${user.apellido}`
      : "Dashboard"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Usuarios", link: "/portal/usuarios" },
      { title: "Registrar Usuario", link: "#" },
    ])
  }, [setBreadcrumbItems, user])

  // ── Fetch empresas from API ──
  useEffect(() => {
    const fetchEmpresas = async () => {
      setEmpresasLoading(true)
      try {
        const endpoint = user?.esSuperAdmin ? "empresas" : "empresas/mis-empresas"
        const res = await axiosClient.get(endpoint)
        setEmpresas(res.data?.data || res.data || [])
      } catch (err) {
        setEmpresas([])
      } finally {
        setEmpresasLoading(false)
      }
    }
    fetchEmpresas()
  }, [user])

  // ── Filtered empresas ──
  const filteredEmpresas = useMemo(() => {
    if (!empresas.length) return []
    if (!searchEmpresa) return empresas
    const term = searchEmpresa.toLowerCase()
    return empresas.filter(
      e =>
        e.nombre?.toLowerCase().includes(term) ||
        e.nit?.includes(term)
    )
  }, [empresas, searchEmpresa])

  // ── Step 1: Validate and go to step 2 ──
  const handleGoToStep2 = e => {
    e.preventDefault()
    setErrorMessage("")
    setSuccessMessage("")

    if (!nombre.trim() || !apellido.trim()) {
      setErrorMessage("Nombre y Apellido son obligatorios")
      return
    }
    if (!email.trim()) {
      setErrorMessage("El email es obligatorio")
      return
    }
    if (!password || password.length < 9) {
      setErrorMessage("La contraseña debe tener mínimo 9 caracteres")
      return
    }

    // If SuperAdmin, create directly (no empresa assignment needed)
    if (esSuperAdmin) {
      handleCreateSuperAdmin()
      return
    }

    setStep(2)
  }

  // ── Create SuperAdmin (no empresas needed) ──
  const handleCreateSuperAdmin = async () => {
    setLoading(true)
    setErrorMessage("")
    try {
      await axiosClient.post("usuarios", {
        email, password, nombre, apellido, telefono, esSuperAdmin: true,
      })
      setSuccessMessage(`Usuario Super Admin "${nombre} ${apellido}" creado exitosamente. Redirigiendo...`)
      setTimeout(() => navigate("/portal/usuarios"), 2000)
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || "Error al crear el usuario"
      setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg))
    } finally {
      setLoading(false)
    }
  }

  // ── Toggle empresa selection ──
  const toggleEmpresa = empresaId => {
    setSelectedEmpresas(prev => {
      const copy = { ...prev }
      if (copy[empresaId]) {
        delete copy[empresaId]
      } else {
        copy[empresaId] = { esAdminEmpresa: false, todasSucursales: true }
      }
      return copy
    })
  }

  const toggleAdminEmpresa = empresaId => {
    setSelectedEmpresas(prev => ({
      ...prev,
      [empresaId]: {
        ...prev[empresaId],
        esAdminEmpresa: !prev[empresaId]?.esAdminEmpresa,
      },
    }))
  }

  const toggleTodasSucursales = empresaId => {
    setSelectedEmpresas(prev => ({
      ...prev,
      [empresaId]: {
        ...prev[empresaId],
        todasSucursales: !prev[empresaId]?.todasSucursales,
      },
    }))
  }

  // ── Step 2 Submit: Create user + assign empresas ──
  const handleCreateAndAssign = async () => {
    const entries = Object.entries(selectedEmpresas)
    if (entries.length === 0) {
      setErrorMessage("Debe seleccionar al menos una empresa")
      return
    }

    setLoading(true)
    setSuccessMessage("")
    setErrorMessage("")

    try {
      // 1. Create user
      const response = await axiosClient.post("usuarios", {
        email, password, nombre, apellido, telefono, esSuperAdmin: false,
      })
      const newUser = response.data.data || response.data
      const createdUserId = newUser.id

      // 2. Assign empresas
      await axiosClient.put(`usuarios/${createdUserId}/empresas`, {
        empresas: entries.map(([empresaId, config]) => ({
          empresaId,
          esAdminEmpresa: config.esAdminEmpresa,
          todasSucursales: config.todasSucursales,
        })),
      })

      setSuccessMessage(`Usuario "${nombre} ${apellido}" creado y empresas asignadas exitosamente. Redirigiendo...`)
      setTimeout(() => navigate("/portal/usuarios"), 2000)
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Error al crear el usuario"
      setErrorMessage(typeof msg === "string" ? msg : JSON.stringify(msg))
    } finally {
      setLoading(false)
    }
  }

  const selectedCount = Object.keys(selectedEmpresas).length

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <Card className="registro-card">
            <CardBody>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0" style={{ fontWeight: 700 }}>
                  Registrar Usuario
                </h4>
                <Button
                  color="outline-secondary"
                  size="sm"
                  onClick={() => navigate("/portal/usuarios")}
                  style={{ borderRadius: 8 }}
                >
                  <i className="mdi mdi-arrow-left me-1" />
                  Volver a Usuarios
                </Button>
              </div>

              {/* Stepper */}
              <div className="registro-stepper">
                <div className={`registro-step ${step === 1 ? "active" : step > 1 ? "completed" : ""}`}>
                  <span className="registro-step-number">
                    {step > 1 ? <i className="mdi mdi-check" /> : "1"}
                  </span>
                  Datos del Usuario
                </div>
                <div className={`registro-step-connector ${step > 1 ? "completed" : ""}`} />
                <div className={`registro-step ${step === 2 ? "active" : ""}`}>
                  <span className="registro-step-number">2</span>
                  Asignar Empresas
                </div>
              </div>

              {/* Messages */}
              {successMessage && (
                <UncontrolledAlert color="success" className="mb-3">
                  <i className="mdi mdi-check-circle me-2" />
                  {successMessage}
                </UncontrolledAlert>
              )}
              {errorMessage && (
                <div className="mb-3" style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}>
                  <i className="mdi mdi-alert-circle" style={{ fontSize: 22, color: "#dc3545", flexShrink: 0 }} />
                  <span style={{ color: "#991b1b", fontSize: "0.9rem", fontWeight: 500, flex: 1 }}>
                    {errorMessage}
                  </span>
                  <button
                    type="button"
                    onClick={() => setErrorMessage("")}
                    style={{ background: "none", border: "none", color: "#991b1b", cursor: "pointer", fontSize: 18, padding: 0 }}
                  >
                    <i className="mdi mdi-close" />
                  </button>
                </div>
              )}

              {/* ═══════════ STEP 1: User Data ═══════════ */}
              {step === 1 && (
                <form onSubmit={handleGoToStep2}>
                  <Row>
                    <Col md={6}>
                      <div className="registro-form-section">
                        <label className="form-label">Nombre</label>
                        <input
                          type="text"
                          className="form-control"
                          value={nombre}
                          onChange={e => setNombre(e.target.value)}
                          placeholder="Ej: Carlos"
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="registro-form-section">
                        <label className="form-label">Apellido</label>
                        <input
                          type="text"
                          className="form-control"
                          value={apellido}
                          onChange={e => setApellido(e.target.value)}
                          placeholder="Ej: Pérez"
                          required
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="registro-form-section">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="usuario@empresa.com"
                          required
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="registro-form-section">
                        <label className="form-label">Contraseña</label>
                        <input
                          type="password"
                          className={`form-control ${password && password.length < 9 ? "is-invalid" : password.length >= 9 ? "is-valid" : ""}`}
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder="Mínimo 9 caracteres"
                          required
                          minLength={9}
                        />
                        {password && password.length < 9 && (
                          <div className="invalid-feedback" style={{ display: "block" }}>
                            <i className="mdi mdi-alert-circle me-1" />
                            La contraseña debe tener mínimo 9 caracteres ({password.length}/9)
                          </div>
                        )}
                        {!password && (
                          <small className="text-muted">Mínimo 9 caracteres</small>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <div className="registro-form-section">
                        <label className="form-label">Teléfono</label>
                        <input
                          type="text"
                          className="form-control"
                          value={telefono}
                          onChange={e => setTelefono(e.target.value)}
                          placeholder="Ej: 3001234567"
                        />
                      </div>
                    </Col>
                    {user?.esSuperAdmin && (
                      <Col md={6}>
                        <div className="registro-form-section d-flex align-items-center gap-3" style={{ paddingTop: 28 }}>
                          <Switch
                            onColor="#556ee6"
                            onChange={setEsSuperAdmin}
                            checked={esSuperAdmin}
                            height={22}
                            width={44}
                          />
                          <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                            Super Administrador
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>

                  <hr className="my-4" />
                  <div className="d-flex justify-content-between">
                    <Button
                      color="outline-secondary"
                      onClick={() => navigate("/portal/usuarios")}
                      style={{ borderRadius: 8 }}
                    >
                      <i className="mdi mdi-arrow-left me-1" />
                      Volver a Usuarios
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={loading}
                      style={{ minWidth: 160, borderRadius: 8 }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Creando...
                        </>
                      ) : esSuperAdmin ? (
                        <>
                          <i className="mdi mdi-check me-1" />
                          Crear Super Admin
                        </>
                      ) : (
                        <>
                          Continuar
                          <i className="mdi mdi-arrow-right ms-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}

              {/* ═══════════ STEP 2: Assign Empresas ═══════════ */}
              {step === 2 && (
                <div>
                  {/* Info banner */}
                  <div style={{
                    background: "#f0f4ff",
                    borderRadius: 8,
                    padding: "12px 16px",
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: "0.85rem",
                    color: "#334155",
                  }}>
                    <i className="mdi mdi-information-outline" style={{ fontSize: 20, color: "#556ee6" }} />
                    <span>
                      Seleccione al menos <strong>una empresa</strong> para asignar al usuario
                      <strong> {nombre} {apellido}</strong>. El usuario se creará al confirmar.
                    </span>
                  </div>

                  {/* Search */}
                  <div className="empresas-search">
                    <i className="mdi mdi-magnify" />
                    <input
                      type="text"
                      value={searchEmpresa}
                      onChange={e => setSearchEmpresa(e.target.value)}
                      placeholder="Buscar empresa por nombre o NIT..."
                    />
                  </div>

                  {/* Summary */}
                  <div className="empresas-summary">
                    <span>
                      <i className="mdi mdi-office-building-outline me-1" />
                      {selectedCount} empresa{selectedCount !== 1 ? "s" : ""} seleccionada{selectedCount !== 1 ? "s" : ""}
                    </span>
                    {selectedCount > 0 && (
                      <button
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => setSelectedEmpresas({})}
                        type="button"
                      >
                        Quitar todas
                      </button>
                    )}
                  </div>

                  {/* Empresas Grid */}
                  {empresasLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Cargando empresas...</p>
                    </div>
                  ) : (
                    <div className="empresas-grid">
                    {filteredEmpresas.map(empresa => {
                      const isSelected = !!selectedEmpresas[empresa.id]
                      const config = selectedEmpresas[empresa.id] || {}

                      return (
                        <div
                          key={empresa.id}
                          className={`empresa-card ${isSelected ? "selected" : ""}`}
                        >
                          {/* Card header */}
                          <div
                            className="empresa-card-header"
                            onClick={() => toggleEmpresa(empresa.id)}
                          >
                            {empresa.logoUrl ? (
                              <img
                                className="empresa-card-logo"
                                src={empresa.logoUrl}
                                alt=""
                                onError={e => { e.target.style.display = "none" }}
                              />
                            ) : (
                              <div className="empresa-card-initial">
                                {empresa.nombre?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <div className="empresa-card-info">
                              <div className="empresa-card-name">{empresa.nombre}</div>
                              <div className="empresa-card-nit">
                                NIT: {empresa.nit}
                                {empresa.digVerificacion ? `-${empresa.digVerificacion}` : ""}
                              </div>
                            </div>
                            <div className="empresa-card-check">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleEmpresa(empresa.id)}
                                style={{ width: 18, height: 18, accentColor: "#556ee6", cursor: "pointer" }}
                              />
                            </div>
                          </div>

                          {/* Toggles (only visible when selected) */}
                          {isSelected && (
                            <div className="empresa-card-toggles">
                              <div className="empresa-toggle-item">
                                <Switch
                                  onColor="#556ee6"
                                  onChange={() => toggleAdminEmpresa(empresa.id)}
                                  checked={config.esAdminEmpresa || false}
                                  height={18}
                                  width={36}
                                  handleDiameter={14}
                                />
                                <span>Admin</span>
                              </div>
                              <div className="empresa-toggle-item">
                                <Switch
                                  onColor="#02a499"
                                  onChange={() => toggleTodasSucursales(empresa.id)}
                                  checked={config.todasSucursales !== false}
                                  height={18}
                                  width={36}
                                  handleDiameter={14}
                                />
                                <span>Todas Sucursales</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  )}

                  {filteredEmpresas.length === 0 && !empresasLoading && (
                    <div className="text-center text-muted py-5">
                      <i className="mdi mdi-magnify-close font-size-24 d-block mb-2" />
                      No se encontraron empresas
                    </div>
                  )}

                  <hr className="my-4" />
                  <div className="d-flex justify-content-between">
                    <Button
                      color="outline-secondary"
                      onClick={() => { setStep(1); setSuccessMessage(""); setErrorMessage("") }}
                      style={{ borderRadius: 8 }}
                      disabled={loading}
                    >
                      <i className="mdi mdi-arrow-left me-1" />
                      Volver
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleCreateAndAssign}
                      disabled={loading || selectedCount === 0}
                      style={{ minWidth: 200, borderRadius: 8 }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Creando usuario...
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-account-check me-1" />
                          Crear y Asignar {selectedCount} Empresa{selectedCount !== 1 ? "s" : ""}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(RegistrarUsuario)
